import { SxProps, Theme } from "@mui/material";

import customTableTokens from "./CustomTable.tokens";

const t = customTableTokens;

/**
 * Every rule that makes a table look like a table lives here, keyed off
 * `theme.palette.table.*` so no colour is ever written literally.
 *
 * Most of it is applied once, on the `<Table>` element, via descendant
 * selectors rather than per-cell `sx`. Two reasons: cells stay markup-only, and
 * the generated class beats the app-wide `MuiTableCell` / `MuiTableRow` theme
 * overrides — which flatten padding and force `background-color: transparent
 * !important` on hover — without every call site having to fight them.
 */

/** Outer card chrome. Rounded, hairline-bordered, clipped so rows can't bleed. */
export const tableCardSx = (theme: Theme): SxProps<Theme> => ({
  position: "relative",
  bgcolor: theme.palette.table.surface,
  border: `${t.card.borderWidth} solid ${theme.palette.table.border}`,
  borderRadius: t.card.radius,
  boxShadow: theme.palette.table.shadow,
  overflow: "hidden",
});

/** Same table, no card — for tables already inside a card, dialog or panel. */
export const tablePlainSx: SxProps<Theme> = {
  position: "relative",
};

/** Title / description block above the column headers. */
export const tableCaptionSx: SxProps<Theme> = {
  px: t.gutter,
  pt: t.caption.paddingTop,
  pb: t.caption.paddingBottom,
  display: "flex",
  flexDirection: "column",
  gap: t.caption.gap,
};

export const tableTitleSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: t.caption.titleFontSize,
  fontWeight: t.caption.titleFontWeight,
  letterSpacing: t.caption.titleLetterSpacing,
  color: theme.palette.table.caption.title,
  lineHeight: "normal",
});

export const tableSubtitleSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: t.caption.descriptionFontSize,
  fontWeight: t.caption.descriptionFontWeight,
  color: theme.palette.table.caption.description,
  lineHeight: "normal",
});

/** Horizontal scroller. Columns keep their width instead of being squashed. */
export const tableContainerSx: SxProps<Theme> = {
  width: "100%",
  overflowX: "auto",
};

/** The one big rule set — see the note at the top of this file. */
export const tableSx = (theme: Theme): SxProps<Theme> => {
  const palette = theme.palette.table;

  return {
    minWidth: t.minWidth,
    borderCollapse: "separate",
    borderSpacing: 0,

    // ---- Column headers -------------------------------------------------
    "& .MuiTableHead-root .MuiTableCell-root": {
      px: 2,
      py: t.header.paddingY,
      bgcolor: palette.header.bg,
      color: palette.header.text,
      fontSize: t.header.fontSize,
      fontWeight: t.header.fontWeight,
      letterSpacing: t.header.letterSpacing,
      textTransform: t.header.textTransform,
      lineHeight: t.header.lineHeight,
      borderTop: `${t.card.borderWidth} solid ${palette.header.border}`,
      borderBottom: `${t.card.borderWidth} solid ${palette.header.border}`,
      whiteSpace: "nowrap",
      "&:first-of-type": { pl: t.gutter },
      "&:last-of-type": { pr: t.gutter },
    },

    // Sort control: inherits the header ink, darkens only when it is the
    // active column so the sorted header reads as the emphasised one.
    "& .MuiTableSortLabel-root": {
      color: "inherit",
      "&:hover": { color: palette.header.activeText },
      "&.Mui-active": {
        color: palette.header.activeText,
        "& .MuiTableSortLabel-icon": {
          color: palette.header.activeText,
          opacity: 1,
        },
      },
    },
    "& .MuiTableSortLabel-icon": {
      fontSize: t.header.sortIconSize,
      opacity: 0.5,
    },

    // ---- Body cells -----------------------------------------------------
    "& .MuiTableBody-root .MuiTableCell-root": {
      px: 2,
      py: t.row.paddingY,
      height: t.row.minHeight,
      color: palette.row.text,
      fontSize: t.row.fontSize,
      fontWeight: t.row.fontWeight,
      lineHeight: t.row.lineHeight,
      borderBottom: `${t.card.borderWidth} solid ${palette.divider}`,
      "&:first-of-type": { pl: t.gutter },
      "&:last-of-type": { pr: t.gutter },
    },

    // The card's own border closes the list, so the last divider is redundant.
    "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
      borderBottom: "none",
    },

    // The no-rows placeholder owns its own padding, so strip the cell's.
    // Declared after the general cell rule so it wins on source order.
    [`& .MuiTableBody-root .${t.empty.cellClass}`]: {
      p: 0,
      borderBottom: "none",
    },

    // ---- Row states -----------------------------------------------------
    // `!important` mirrors the app-wide MuiTableRow override it has to beat.
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: t.row.transition,
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${palette.row.hover} !important`,
    },
    "& .MuiTableBody-root .MuiTableRow-root.Mui-selected": {
      backgroundColor: `${palette.row.selected} !important`,
      "&:hover": { backgroundColor: `${palette.row.selected} !important` },
    },

    // ---- Selection controls --------------------------------------------
    // See the note on `selection` in the tokens file — the padding is what
    // makes the header strip tall, not the cell's own spacing.
    "& .MuiCheckbox-root, & .MuiRadio-root": {
      p: t.selection.padding,
      color: palette.selection.rest,
      "&.Mui-checked, &.MuiCheckbox-indeterminate": {
        color: palette.selection.active,
      },
      "& svg": {
        width: t.selection.glyphSize,
        height: t.selection.glyphSize,
        display: "block",
      },
    },

    // ---- Icons & action columns -----------------------------------------
    // Scoped to icon buttons: a bare size rule here would also shrink status
    // and inline glyphs the screens render inside ordinary cells.
    "& .MuiTableBody-root .MuiIconButton-root": {
      color: palette.icon.rest,
      transition: t.row.transition,
      "&:hover": {
        color: palette.icon.hover,
        backgroundColor: palette.row.selected,
      },
      "& .MuiSvgIcon-root": { fontSize: t.action.iconSize },
    },
  };
};

/** "N selected" counter beside the header checkbox. */
export const selectedCountSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: t.header.fontSize,
  fontWeight: t.header.fontWeight,
  letterSpacing: t.header.letterSpacing,
  color: theme.palette.table.selection.countText,
  whiteSpace: "nowrap",
});

/** Default no-rows message. */
export const emptyStateSx = (theme: Theme): SxProps<Theme> => ({
  px: t.gutter,
  py: t.empty.paddingY,
  textAlign: "center",
  fontSize: t.empty.fontSize,
  fontWeight: t.empty.fontWeight,
  color: theme.palette.table.empty.text,
  lineHeight: "normal",
});

/** Illustration shown when a screen supplies no custom empty state. */
export const emptyImageSx: SxProps<Theme> = {
  height: t.empty.imageHeight,
  maxWidth: "100%",
  objectFit: "contain",
};

/**
 * Footer strip under the rows. Only the divider — the padding and alignment
 * live on `CustomPagination` itself so the control looks the same when a
 * screen renders it outside a table.
 */
export const paginationBarSx = (theme: Theme): SxProps<Theme> => ({
  borderTop: `${t.card.borderWidth} solid ${theme.palette.table.pagination.border}`,
});

/** Right-aligned row the page control sits in, inside a table or standalone. */
export const paginationLayoutSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  px: t.gutter,
  py: t.pagination.paddingY,
};

/** MUI `<Pagination>` restyled to the design's square, flat page items. */
export const paginationSx = (theme: Theme): SxProps<Theme> => {
  const palette = theme.palette.table.pagination;

  return {
    "& .MuiPagination-ul": { gap: t.pagination.itemGap, flexWrap: "nowrap" },
    "& .MuiPagination-ul > li": { m: 0 },
    "& .MuiPaginationItem-root": {
      minWidth: t.pagination.itemSize,
      width: t.pagination.itemSize,
      height: t.pagination.itemSize,
      m: 0,
      p: 0,
      borderRadius: t.pagination.itemRadius,
      bgcolor: "transparent",
      fontSize: t.pagination.fontSize,
      fontWeight: t.pagination.fontWeight,
      color: palette.text,
      transition: t.row.transition,
      "&:hover": { bgcolor: palette.hover },
    },
    "& .MuiPaginationItem-root.Mui-selected": {
      bgcolor: palette.selectedBg,
      color: palette.selectedText,
      fontWeight: t.pagination.selectedFontWeight,
      "&:hover": { bgcolor: palette.selectedBg, color: palette.selectedText },
    },
    "& .MuiPaginationItem-ellipsis": {
      width: "auto",
      minWidth: "unset",
      px: "4px",
      height: t.pagination.itemSize,
      display: "flex",
      alignItems: "center",
      color: palette.ellipsis,
      bgcolor: "transparent",
      fontSize: t.pagination.fontSize,
      fontWeight: t.caption.descriptionFontWeight,
    },
    "& .MuiPaginationItem-icon": {
      width: t.pagination.arrowSize,
      height: t.pagination.arrowSize,
      fontSize: t.pagination.arrowSize,
    },
    "& .MuiPaginationItem-root.Mui-disabled": {
      color: palette.disabled,
      opacity: 1,
    },
  };
};
